import { Button } from "react-bootstrap";
import { formatPrice } from "utils/helpers/functions/formatPrice";
import { withLocalCurrency, withPrimaryCurrency } from "utils/helpers/currency/currencyHelpers";
import { ProductTitle } from 'modules/product';
import CountBadge from "../CounBadge";
import { safeFloatParse, safeIntParse } from "utils/helpers/safeParsing";
import { useSettings } from "context/SettingsContext";
import { useState, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import FormInput from "components/forms/FormInput";
import { useTranslation } from "react-i18next";
import type { WarehouseComingRowType } from "./model/types";
import SolarIcon from 'components/icons/SolarIcon';

interface ComingTableBodyProps {
    aComing: WarehouseComingRowType[];
    setAComing: Dispatch<SetStateAction<WarehouseComingRowType[]>>;
    comingProcess: boolean;
    editable?: boolean;
    showPayment?: boolean;
    hidePrice?: boolean;
    getMaxCount?: (row: WarehouseComingRowType) => number | null | undefined;
}

const ComingTableBody = ({
    aComing,
    setAComing,
    comingProcess,
    editable = true,
    showPayment = false,
    hidePrice = false,
    getMaxCount,
}: ComingTableBodyProps) => {
    const { localSettings } = useSettings();
    const { t } = useTranslation(['Warehouses', 'common']);
    const rate = Number(localSettings?.RATE?.current || 1);
    const isUahMode = (Number(localSettings?.RATE?.current ?? 1) || 1) === 1;
    const [editPriceIndex, setEditPriceIndex] = useState<number | null>(null);
    const [editCountIndex, setEditCountIndex] = useState<number | null>(null);

    const handleClickDelete = (e: MouseEvent<HTMLButtonElement>, index: number) => {
        setAComing((prev) => prev.filter((_, i) => i !== index));
        e.currentTarget.blur();
    }

    const handleChangeRow = (index: number, patch: Partial<WarehouseComingRowType['coming']>) => {
        setAComing((prev) =>
            prev.map((row, i) =>
                i !== index
                    ? row
                    : {
                          ...row,
                          coming: {
                              ...row.coming,
                              ...patch,
                          },
                      },
            ),
        );
    };

    const getPaymentFileUrl = (coming: WarehouseComingRowType): string => {
        const aiUrl = String(coming?.coming?.aiMeta?.cloudFile?.publicUrl || '');
        if (aiUrl) return aiUrl;
        const cloudFiles = coming?.coming?.paymentAttachmentsCloud;
        if (Array.isArray(cloudFiles) && cloudFiles.length) {
            return String(cloudFiles[0]?.publicUrl || '');
        }
        return '';
    };

    const buildPaymentDescriptionFromAi = (coming: WarehouseComingRowType): string => {
        const aiMeta = coming?.coming?.aiMeta;
        if (!aiMeta?.fileName) return String(coming?.coming?.paymentDescription || '');
        return `${t('labelAiDocPrefix')}: ${aiMeta.fileName}, ${t('labelAiDocLine')}: ${Number(aiMeta.sourceLine || 0)}`;
    };

    const handleTogglePayment = (index: number, checked: boolean, coming: WarehouseComingRowType) => {
        if (!checked) {
            handleChangeRow(index, {
                payment: false,
                paymentDescription: '',
                paymentAttachmentsCloud: [],
            });
            return;
        }

        const cloudFile = coming?.coming?.aiMeta?.cloudFile;
        const existingCloud = Array.isArray(coming?.coming?.paymentAttachmentsCloud)
            ? coming.coming.paymentAttachmentsCloud
            : [];
        handleChangeRow(index, {
            payment: true,
            paymentDescription:
                String(coming?.coming?.paymentDescription || '').trim() ||
                buildPaymentDescriptionFromAi(coming),
            paymentAttachmentsCloud:
                existingCloud.length > 0
                    ? existingCloud
                    : cloudFile
                      ? [cloudFile]
                      : [],
        });
    };

    const handleOpenPaymentFile = (e: MouseEvent, coming: WarehouseComingRowType) => {
        e.preventDefault();
        e.stopPropagation();
        const fileUrl = getPaymentFileUrl(coming);
        if (!fileUrl) return;
        if (typeof window !== 'undefined') {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
        }
    };
 
    return (
        <tbody>
            {aComing?.map((coming,index)=>{
                const price = formatPrice(coming?.coming?.price);
                const priceRate = formatPrice(coming?.coming?.priceRate);
                const canEdit = editable && !comingProcess;
                const paymentFileUrl = getPaymentFileUrl(coming);
                return (
                    <tr key={`${coming.product?.ID ?? 'row'}-${index}`}>
                        <td className="name">
                            <ProductTitle
                                product={{
                                    prodName:coming.product.label,
                                    vendorName:coming.product.vendor,
                                    prodStatus: 1
                                } as any}/>
                        </td> 
                        
                        {!hidePrice && 
                            (<td className="price">
                                {canEdit && editPriceIndex === index ? (
                                    <FormInput
                                        autoFocus={true}
                                        autoSelect={true}
                                        compact={true}
                                        showForm={true}
                                        setShowForm={(show) => {
                                            if (!show) setEditPriceIndex(null);
                                        }}
                                        defaultValue={Number(coming?.coming?.price || 0)}
                                        decimalPlaces={2}
                                        callBack={(value) => {
                                            
                                            const nextPrice = safeFloatParse(value || 0) ?? 0;
                                            handleChangeRow(index, {
                                                price: nextPrice,
                                                priceRate: safeFloatParse(nextPrice * rate) ?? 0,
                                            });
                                            setEditPriceIndex(null);
                                        }}
                                    />
                                ) : (
                                    <h6
                                        className={`mb-1 text-break${canEdit ? ' cursor-pointer' : ''}`}
                                        onClick={() => canEdit && setEditPriceIndex(index)}
                                    >
                                        {withPrimaryCurrency(price, rate)}
                                    </h6>
                                )}
                                {!isUahMode && (
                                    <p className="text-muted f-12 mb-0 my-1">{withLocalCurrency(priceRate)}</p>
                                )}
                                {coming?.coming?.aiMeta?.mode === 'new' && (
                                    <span className="badge bg-light-warning mt-1">{t('aiBadge.new')}</span>
                                )}
                                {coming?.coming?.aiMeta?.mode === 'similar' && (
                                    <span className="badge bg-light-info mt-1">{t('aiBadge.similar')}</span>
                                )}
                                {coming?.coming?.aiMeta?.mode === 'exact' && (
                                    <span className="badge bg-light-success mt-1">{t('aiBadge.exact')}</span>
                                )}
                            </td>)} 

                        <td className={`count text-end fw-medium ${Number(coming.coming.cCount) < 0 ? '_text-danger' : ''}`}>
                            {canEdit && editCountIndex === index ? (
                                <FormInput
                                    compact={true} 
                                    autoFocus={true}
                                    autoSelect={true}
                                    showForm={true}
                                    setShowForm={(show) => {
                                        if (!show) setEditCountIndex(null);
                                    }}
                                    defaultValue={Number(coming?.coming?.cCount || 0)}
                                    numbersOnly={true}
                                    callBack={(value) => {
                                         
                                        let nextCount = safeIntParse(value) ?? 0;
                                        const maxRaw =
                                            typeof getMaxCount === 'function'
                                                ? getMaxCount(coming)
                                                : null;
                                        const max = Number(maxRaw);
                                        
                                        if (Number.isFinite(max) && max >= 0 && (maxRaw!==null)) {
                                            nextCount = Math.min(nextCount, Math.floor(max));
                                        }
                                         
                                        if (nextCount < 0) nextCount = 0;
                                        handleChangeRow(index, {
                                            cCount: nextCount,
                                        });
                                        setEditCountIndex(null);
                                    }}
                                />
                            ) : (
                                <div className="d-flex justify-content-end align-items-center">
                                    <div
                                        onClick={() => canEdit && setEditCountIndex(index)}
                                        className={canEdit ? 'cursor-pointer' : undefined}
                                    >
                                        <CountBadge count={coming.coming.cCount}/>
                                    </div>
                                </div>
                            )}
                        </td>
                        {showPayment && (
                            <td className="payment text-center">
                                <div className="d-inline-flex align-items-center gap-1">
                                    <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={Boolean(coming?.coming?.payment)}
                                        disabled={!canEdit}
                                        onChange={(e) => handleTogglePayment(index, e.target.checked, coming)}
                                        aria-label={t('labelPayment')}
                                    />
                                    {Boolean(coming?.coming?.payment) && paymentFileUrl ? (
                                        <span
                                            className="d-inline-flex text-success cursor-pointer"
                                            onClick={(e) => handleOpenPaymentFile(e, coming)}
                                            role="button"
                                            title={t('common:labelAttachments')}
                                        >
                                            <SolarIcon name="paperclip" />
                                        </span>
                                    ) : null}
                                </div>
                            </td>
                        )}
                        <td className="action">
                            <Button
                                size="sm"  
                                className="py-0 px-1 mb-1"
                                variant="light-danger"
                                disabled={comingProcess}
                                onClick={(e)=>handleClickDelete(e,index)}>
                                <SolarIcon name="trash3" />
                            </Button>
                        </td>
                    </tr>
                )
            })}
           
        </tbody> 
    )
}

export default ComingTableBody;
